import type { HttpPort } from "~/lib/http";

export type VotingStatus = "open" | "closed" | "results";

export interface VotingSession {
  id: string;
  week_start: string;
  week_end: string;
  status: VotingStatus;
}

export interface VotingGoal {
  id: string;
  title: string;
  description: string | null;
  category_icon?: string | null;
}

export interface VotingOption extends VotingGoal {
  goal_pool_id: string;
  display_order: number;
  vote_count: number | string;
}

export interface RankedGoal {
  id?: string;
  rank: number | string;
  title: string;
}

export interface LastWeekGoal {
  rank: number | string | null;
  title: string;
  description?: string | null;
}

interface CurrentVotingResponse {
  session: VotingSession | null;
  options?: VotingOption[];
  participationCount?: number;
  totalStudents?: number;
  lastWeekGoals?: LastWeekGoal[];
}

export type VotingSnapshot =
  | { kind: "start"; goals: VotingGoal[]; lastWeekGoals: LastWeekGoal[] }
  | {
      kind: "active";
      session: VotingSession;
      options: VotingOption[];
      participationCount: number;
      totalStudents: number;
      absentCount: number;
      activeGoals: RankedGoal[];
    };

export type VotingCommand =
  | { type: "start"; goalIds: string[] }
  | { type: "close-and-activate"; sessionId: string }
  | { type: "activate"; sessionId: string }
  | { type: "reset"; sessionId: string };

export function createVotingModule(http: HttpPort, token: string) {
  return {
    async load(classId: string): Promise<VotingSnapshot> {
      const [current, today] = await Promise.all([
        http.request<CurrentVotingResponse>({ path: `/voting/${classId}/current`, token }),
        http.request<{ not_checked_in?: Array<{ is_absent?: boolean }> }>({ path: `/classes/${classId}/today`, token }).catch(() => null)
      ]);

      if (!current.session) {
        const pool = await http.request<{ goals?: VotingGoal[] }>({ path: `/goals/${classId}/pool`, token });
        return { kind: "start", goals: pool.goals ?? [], lastWeekGoals: current.lastWeekGoals ?? [] };
      }

      const activeGoals = current.session.status === "results"
        ? await http.request<{ activeGoals?: RankedGoal[] }>({ path: `/goals/${classId}/active`, token }).then((value) => value.activeGoals ?? []).catch(() => [])
        : [];
      return {
        kind: "active",
        session: current.session,
        options: current.options ?? [],
        participationCount: current.participationCount ?? 0,
        totalStudents: current.totalStudents ?? 0,
        absentCount: today?.not_checked_in?.filter((student) => student.is_absent === true).length ?? 0,
        activeGoals
      };
    },

    async execute(classId: string, command: VotingCommand): Promise<{ message?: string }> {
      if (command.type === "start") {
        if (command.goalIds.length < 4 || command.goalIds.length > 8) throw new Error("4–8 Ziele auswählen");
        return http.request({ path: `/voting/${classId}/create`, method: "POST", token, body: { goalIds: command.goalIds } });
      }
      if (command.type === "close-and-activate") {
        await http.request({ path: `/voting/${classId}/close/${command.sessionId}`, method: "POST", token, body: {} });
        return http.request({ path: `/goals/${classId}/active`, method: "POST", token, body: { sessionId: command.sessionId } });
      }
      if (command.type === "activate") {
        return http.request({ path: `/goals/${classId}/active`, method: "POST", token, body: { sessionId: command.sessionId } });
      }
      return http.request({ path: `/voting/${classId}/reset/${command.sessionId}`, method: "DELETE", token });
    }
  };
}

export type VotingModule = ReturnType<typeof createVotingModule>;

export function randomGoalIds(goals: VotingGoal[], random: () => number = Math.random): string[] {
  const values = [...goals];
  for (let index = values.length - 1; index > 0; index -= 1) {
    const target = Math.floor(random() * (index + 1));
    [values[index], values[target]] = [values[target], values[index]];
  }
  return values.slice(0, 8).map((goal) => goal.id);
}

export function formatVotingDate(value: string): string {
  return new Date(value).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });
}
